import {useEffect, useRef} from "react";
import {Skeleton} from "@mantine/core";
import {useChecklistData} from "./ChecklistDataContext";
import {renderNode} from "../renderNode";
import {CheckItemParent} from "./components/CheckItemParent";
import {CheckItemLeafNode} from "./components/CheckItemLeafNode";
import {CheckItemIngress} from "./components/CheckItemIngress";
import {CheckItemDescription} from "./components/CheckItemDescription";
import {DocumentGeneratorRefCard} from "./components/DocumentGeneratorRefCard";
import {FileRefCard} from "./components/FileRefCard";
import {
    ChecklistItemType,
    ResourceType,
    type ChecklistFileItem,
    type ChecklistRelatedItem,
} from "../types";
import {isValidFileType} from "../contentUtils";
import {getStatusNames} from "./statusNames";
import styles from "./checklist.module.css";

export const CheckItem = ({
    itemId,
    parentId,
    isLoading,
}: {
    itemId: string;
    parentId: string;
    isLoading?: boolean;
}) => {
    const {
        viewFilteredToc,
        isHidden,
        isSearchHit,
        itemsState,
        onClickItem,
        observeChecklistItemRef,
        unobserveChecklistItemRef,
        topicData,
    } = useChecklistData();

    const itemRef = useRef<HTMLDivElement>(null);
    const node = viewFilteredToc[itemId];

    const itemType = node?.checkItemType;
    const isParent = itemType === ChecklistItemType.CheckItemParent;
    const isOpen = isParent ? (itemsState.openItems[itemId] ?? false) : true;
    const hasChildren = (node?.children?.length ?? 0) > 0;

    useEffect(() => {
        const el = itemRef.current;
        if (el) {
            if (isOpen && hasChildren) {
                observeChecklistItemRef(el);
            } else {
                unobserveChecklistItemRef(el);
            }
        }
        return () => {
            if (el) {
                unobserveChecklistItemRef(el);
            }
        };
        // Callbacks are stable by design (useIntersectionObserver uses refs internally).
        // Including them would cause a render loop since they get new references each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, hasChildren]);

    if (!node) return null;

    const currentIsLoading =
        isLoading || (itemsState.loadingState[itemId]?.isLoading ?? false);
    const hasElements =
        (node.ingress && itemsState.elements[node.ingress]) ||
        (node.description && itemsState.elements[node.description]) ||
        (node.law && itemsState.elements[node.law]);

    const renderContentSections = () => {
        if (!isOpen) return null;
        if (!node.ingress && !node.description && !node.law) return null;

        if (currentIsLoading && !hasElements) {
            return (
                <div className={styles.contentSections}>
                    <div className={styles.loadingIndicator}>
                        <Skeleton height={12} mb="sm" />
                        <Skeleton height={12} mb="sm" />
                        <Skeleton height={12} width="70%" />
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.contentSections} data-searchable="true">
                {node.ingress &&
                    itemsState.elements[node.ingress] && (
                        <CheckItemIngress ingress={node.ingress} />
                    )}
                {node.law &&
                    itemsState.elements[node.law] && (
                        <div className={styles.contentBody}>
                            <div className={styles.lawSection}>
                                {renderNode(topicData, node.law, {}, node.law)}
                            </div>
                        </div>
                    )}
                {node.description &&
                    itemsState.elements[node.description] && (
                        <CheckItemDescription
                            description={node.description}
                            forceExpanded={isSearchHit(itemId)}
                        />
                    )}
            </div>
        );
    };

    const renderChildren = () => {
        if (!hasChildren || !isOpen || !isParent) return null;

        const visibleChildren = node.children!.filter(
            (childId) => !isHidden(childId),
        );

        return (
            <div data-pw="check-item-children">
                {visibleChildren.map((childId) => (
                    <CheckItem
                        key={childId}
                        itemId={childId}
                        parentId={itemId}
                        isLoading={currentIsLoading}
                    />
                ))}
            </div>
        );
    };

    const renderRelatedItems = () => {
        if (!isOpen || !node.related || node.related.length === 0) return null;

        const visibleRelated = node.related.filter(
            (rel) =>
                rel.resourceTypeId === ResourceType.documentGenerator ||
                rel.resourceTypeId === ResourceType.checklist,
        );

        if (visibleRelated.length === 0) return null;

        return (
            <div className={styles.relatedSection}>
                <ul>
                    {visibleRelated.map((rel) => (
                        <li key={rel.topicId}>
                            <DocumentGeneratorRefCard relatedItem={rel} />
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const renderFilesItems = () => {
        if (!isOpen || !node.files || node.files.length === 0) return null;

        const pairs = node.files
            .map((file) => {
                const relatedItem = node.related?.find(
                    (r) => r.topicId === file.topicId,
                );
                return relatedItem ? {file, relatedItem} : null;
            })
            .filter(
                (
                    p,
                ): p is {
                    file: ChecklistFileItem;
                    relatedItem: ChecklistRelatedItem;
                } => !!p && isValidFileType(p.relatedItem.resourceTypeId),
            );

        if (pairs.length === 0) return null;

        return (
            <div className={styles.filesSection}>
                <ul>
                    {pairs.map(({file, relatedItem}) => (
                        <li key={file.id}>
                            <FileRefCard
                                fileItem={file}
                                relatedItem={relatedItem}
                            />
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const handleClick = () => onClickItem(itemId, parentId);

    if (isParent) {
        return (
            <CheckItemParent
                ref={itemRef}
                itemId={itemId}
                isOpen={isOpen}
                onTitleClick={handleClick}
                names={getStatusNames()}
            >
                {isOpen && (
                    <div className={styles.checkItemContent}>
                        {renderContentSections()}
                        {renderRelatedItems()}
                        {renderFilesItems()}
                        {renderChildren()}
                    </div>
                )}
            </CheckItemParent>
        );
    }

    return (
        <CheckItemLeafNode itemId={itemId} names={getStatusNames()}>
            <div className={styles.checkItemContent}>
                {renderContentSections()}
                {renderRelatedItems()}
                {renderFilesItems()}
            </div>
        </CheckItemLeafNode>
    );
};
